import React, {
  useEffect,
  useCallback,
  useRef,
  useContext,
  useState,
} from "react";
import { Container } from "components";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumb from "components/molecules/Breadcrumb";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  API_URL_agent,
  API_URL_department,
  API_URL_slaplan,
  API_URL_ticketPriority,
} from "constants/api";
import { departmentReducers } from "redux/departmentSlice";
import { slaplanReducers } from "redux/slaplanSlice";
import { getData, postData, putData } from "actions";
import { agentReducers } from "redux/agentSlice";
import { AuthContext } from "context/AuthContext";
import { ticketPriorityReducers } from "redux/ticketPrioritySlice";

const FormDepartments = ({ typeForm }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [adminAccess, setAdminAccess] = useState(false);
  const { datauser } = useContext(AuthContext);
  const dispatch = useDispatch();
  const inputRefs = useRef([]);

  const {
    getSlaplanResult,
    getAgentResult,
    getTicketPriorityResult,
  } = useSelector((state) => ({
    getSlaplanResult: state.slaplan.getSlaplanResult,
    getAgentResult: state.agent.getAgentResult,
    getTicketPriorityResult: state.ticketPriority.getTicketPriorityResult,
  }));

  const getSlaplan = useCallback(
    async (params) => {
      getData(
        API_URL_slaplan,
        params,
        { dispatch, redux: slaplanReducers },
        "GET_SLAPLAN"
      );
    },
    [dispatch]
  );

  const getPriority = useCallback(
    async (params) => {
      getData(
        API_URL_ticketPriority,
        params,
        { dispatch, redux: ticketPriorityReducers },
        "GET_TICKETPRIORITY"
      );
    },
    [dispatch]
  );

  const getAgent = useCallback(
    async (params) => {
      getData(
        API_URL_agent,
        params,
        { dispatch, redux: agentReducers },
        "GET_AGENT"
      );
    },
    [dispatch]
  );

  const formik = useFormik({
    initialValues: {
      name: location.state ? location.state.item.name : "",
      type: location.state ? location.state.item.type : "",
      sla: location.state ? location.state.item.sla : "",
      manager: location.state ? location.state.item.manager : "",
      department_default: location.state
        ? location.state.item.department_default
        : 0,
      priority: location.state ? location.state.item.priority : "",
      auto_assign: location.state ? location.state.item.auto_assign : "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      type: Yup.string().required("Type is required"),
      manager: Yup.string().required("Manager is required"),
      sla: Yup.string().required("Sla is required"),
      priority: Yup.string().required("Priority is required"),
    }),
    onSubmit: (values) => {
      const formData = values;
      if (location.state === null) {
        postData(
          API_URL_department,
          formData,
          { dispatch, redux: departmentReducers },
          "ADD_DEPARTMENT",
          () => {
            navigate("/administrator/staff/departments");
          }
        );
      } else {
        putData(
          `${API_URL_department}${location.state.item.id}/`,
          formData,
          { dispatch, redux: departmentReducers },
          "ADD_DEPARTMENT",
          () => {
            navigate("/administrator/staff/departments");
          }
        );
      }
    },
  });

  // Focus Input validate & submit
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    await formik.validateForm();
    const firstInvalidInputRef = inputRefs.current.find((inputRef) => {
      const fieldName = inputRef.name;
      formik.setFieldTouched(fieldName, true, true);
      return formik.errors[fieldName];
    });
    if (firstInvalidInputRef) {
      firstInvalidInputRef.focus();
    } else {
      formik.handleSubmit();
    }
  };

  useEffect(() => {
    const params = `?limit=${9999999}`;
    const agentParams = `?limit=${9999999}&role_group=!user&is_active=1`;
    getSlaplan(params);
    getAgent(agentParams);
    getPriority(params);
  }, []);

  useEffect(() => {
    if (datauser !== "") {
      const permissionViewMenuAdmin = datauser.group_permissions.includes(
        "access_menu_admin"
      );
      if (permissionViewMenuAdmin) {
        setAdminAccess(true);
      } else {
        navigate("/dashboard/agent");
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <Breadcrumb
        title={"Departments"}
        excludedPaths={["staff", "administrator"]}
        group={"Superadmin"}
      />
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
          Create an department
        </div>
        <form onSubmit={handleFormSubmit}>
          <div className="h-auto w-auto px-4 bg-neutral dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-600 object-left">
            <div className="mt-4 grid grid-cols-12 gap-3">
              <div className="col-span-full mb-1.5 md:col-span-6 md:col-start-1 lg:col-span-6 lg:col-start-1">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Nama <span className="text-red-500">*</span>
                </label>
                <div>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[0] = ref)}
                    value={formik.values.name}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                  />
                  {formik.touched.name && formik.errors.name && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.name}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-6 md:col-start-7 lg:col-span-6 lg:col-start-7">
                <label className="text-xs md:-text-sm lg:text-sm">
                  Type <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-col flex-wrap">
                  <div>
                    <label className="inline-flex items-center mt-2 text-xs md:-text-sm lg:text-sm">
                      <input
                        id="type"
                        type="radio"
                        name="type"
                        checked={formik.values.type === 1}
                        ref={(ref) => (inputRefs.current[1] = ref)}
                        onChange={() => formik.setFieldValue("type", 1)}
                        className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                      />
                      <span className="ml-2 mr-5">Private</span>
                    </label>
                    <label className="inline-flex items-center mt-2 text-xs md:-text-sm lg:text-sm">
                      <input
                        id="type"
                        type="radio"
                        name="type"
                        ref={(ref) => (inputRefs.current[2] = ref)}
                        checked={formik.values.type === 0}
                        onChange={() => formik.setFieldValue("type", 0)}
                        className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                      />
                      <span className="ml-2">Public</span>
                    </label>
                  </div>
                  {formik.touched.type && formik.errors.type && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.type}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-6 md:col-start-1 lg:col-span-6 lg:col-start-1">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  SLA Plan <span className="text-red-500">*</span>
                </label>
                <div>
                  <select
                    id="sla"
                    name="sla"
                    ref={(ref) => (inputRefs.current[3] = ref)}
                    value={formik.values.sla}
                    onChange={formik.handleChange}
                    className="block w-full rounded-md border border-gray-500 bg-white py-2 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                  >
                    <option value="">Select a SLA</option>
                    {getSlaplanResult.results &&
                      getSlaplanResult.results.map((item, index) => (
                        <option key={index} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                  </select>
                  {formik.touched.sla && formik.errors.sla && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.sla}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-6 md:col-start-7 lg:col-span-6 lg:col-start-7">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Manager <span className="text-red-500">*</span>
                </label>
                <div>
                  <select
                    id="manager"
                    name="manager"
                    value={formik.values.manager}
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[4] = ref)}
                    className="block w-full rounded-md border border-gray-500 bg-white py-2 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                  >
                    <option value="">Select a manager</option>
                    {getAgentResult.results &&
                      getAgentResult.results.map((item, index) => (
                        <option key={index} value={item.id}>
                          {item.first_name + " " + item.last_name}
                        </option>
                      ))}
                  </select>
                  {formik.touched.manager && formik.errors.manager && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.manager}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-6 md:col-start-1 lg:col-span-6 lg:col-start-1">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Auto Assign
                </label>
                <div>
                  <select
                    id="auto_assign"
                    name="auto_assign"
                    value={formik.values.auto_assign}
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[5] = ref)}
                    className="block w-full rounded-md border border-gray-500 bg-white py-2 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                  >
                    <option value="">Select a Assign</option>
                    {getAgentResult.results &&
                      getAgentResult.results.map((item, index) => (
                        <option key={index} value={item.id}>
                          {item.first_name + " " + item.last_name}
                        </option>
                      ))}
                  </select>
                  {formik.touched.auto_assign && formik.errors.auto_assign && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.auto_assign}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-6 md:col-start-7 lg:col-span-6 lg:col-start-7">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Priority Default <span className="text-red-500">*</span>
                </label>
                <div>
                  <select
                    id="priority"
                    name="priority"
                    value={formik.values.priority}
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[6] = ref)}
                    className="block w-full rounded-md border border-gray-500 bg-white py-2 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                  >
                    <option value="">Select a Priority</option>
                    {getTicketPriorityResult.results &&
                      getTicketPriorityResult.results.map((item, index) => (
                        <option key={index} value={item.id}>
                          {item.priority}
                        </option>
                      ))}
                  </select>
                  {formik.touched.priority && formik.errors.priority && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.priority}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="h-12 w-full grid items-center px-4 bg-neutral-50 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
            <button
              type="submit"
              className="w-20 items-center p-1 px-3 rounded shadow-sm border text-white bg-blue-800 hover:bg-blue-700"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </Container>
  );
};

export default FormDepartments;
